import { useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Loader2, BookOpen, Award, ChevronDown, GraduationCap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, DOC_META } from "@/lib/api";

const SEM_LABELS = [
  { n: 1, label: "Semester 1 (Kelas VII Ganjil)" },
  { n: 2, label: "Semester 2 (Kelas VII Genap)" },
  { n: 3, label: "Semester 3 (Kelas VIII Ganjil)" },
  { n: 4, label: "Semester 4 (Kelas VIII Genap)" },
  { n: 5, label: "Semester 5 (Kelas IX Ganjil)" },
  { n: 6, label: "Semester 6 (Kelas IX Genap) + Kelulusan" },
];

export default function DownloadPanel({ student }) {
  const [busy, setBusy] = useState("");

  const download = async (endpoint, filename) => {
    setBusy(endpoint);
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunduh");
    } finally {
      setBusy("");
    }
  };

  const preview = async (endpoint) => {
    setBusy(endpoint);
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuka pratinjau");
    } finally {
      setBusy("");
    }
  };

  const nisn = student.nisn;
  const nama = (student.nama_lengkap || "siswa").replace(/\s+/g, "_");
  const docs = student.documents || {};

  return (
    <Card data-testid="download-panel">
      <CardHeader>
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <Download className="w-5 h-5 text-blue-700" /> Unduh Buku Induk & Berkas
        </CardTitle>
        <CardDescription>
          Setiap semester tersedia sebagai lembar PDF terpisah. Dokumen pendukung (KK, Akta, Ijazah, Pas Foto) diunduh sebagai <strong>berkas foto asli</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Combined + Biodata + Kelulusan row */}
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Buku Induk Lengkap</div>
          <div className="grid sm:grid-cols-3 gap-2">
            <DlButton
              busy={busy === `/admin/students/${student.id}/pdf`}
              onClick={() => download(`/admin/students/${student.id}/pdf`, `Buku_Induk_${nisn}_${nama}.pdf`)}
              icon={<BookOpen className="w-4 h-4" />}
              label="Buku Induk (8 hal)"
              primary
              testid="dl-full"
            />
            <DlButton
              busy={busy === `/admin/students/${student.id}/pdf/biodata`}
              onClick={() => download(`/admin/students/${student.id}/pdf/biodata`, `Biodata_${nisn}_${nama}.pdf`)}
              icon={<FileText className="w-4 h-4" />}
              label="Biodata (Hal 1-2)"
              testid="dl-biodata"
            />
            <DlButton
              busy={busy === `/admin/students/${student.id}/pdf/kelulusan`}
              onClick={() => download(`/admin/students/${student.id}/pdf/kelulusan`, `Kelulusan_${nisn}_${nama}.pdf`)}
              icon={<Award className="w-4 h-4" />}
              label="Lembar Kelulusan"
              testid="dl-kelulusan"
            />
          </div>
        </div>

        {/* Semester per-page */}
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Lembar Nilai Per Semester</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {SEM_LABELS.map((s) => {
              const has = student.semesters?.[String(s.n)]?.grades?.length > 0;
              return (
                <DlButton
                  key={s.n}
                  busy={busy === `/admin/students/${student.id}/pdf/semester/${s.n}`}
                  onClick={() => download(`/admin/students/${student.id}/pdf/semester/${s.n}`, `Semester_${s.n}_${nisn}_${nama}.pdf`)}
                  icon={<GraduationCap className="w-4 h-4" />}
                  label={s.label}
                  sublabel={has ? "Data terisi dari Leger" : "Belum ada nilai — cetak kosong"}
                  filled={has}
                  testid={`dl-sem-${s.n}`}
                />
              );
            })}
          </div>
        </div>

        {/* Dokumen berkas asli (bukan wrap PDF) */}
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Dokumen Pendukung (Berkas Asli)</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {Object.entries(DOC_META).map(([type, meta]) => {
              const doc = docs[type];
              const ext = (() => {
                const ct = (doc?.content_type || "").toLowerCase();
                if (ct.includes("png")) return "png";
                if (ct.includes("webp")) return "webp";
                if (ct.includes("pdf")) return "pdf";
                if (ct.includes("jpeg") || ct.includes("jpg")) return "jpg";
                const fn = doc?.filename || "";
                const m = fn.match(/\.([a-zA-Z0-9]+)$/);
                return (m ? m[1] : "jpg").toLowerCase();
              })();
              return (
                <DlButton
                  key={type}
                  busy={busy === `/admin/students/${student.id}/documents/${type}/view`}
                  onClick={() => doc && download(`/admin/students/${student.id}/documents/${type}/view`, `${meta.label.replace(/\s+/g, "_")}_${nisn}_${nama}.${ext}`)}
                  disabled={!doc}
                  icon={<FileText className="w-4 h-4" />}
                  label={meta.label}
                  sublabel={doc ? `${(doc.size / 1024).toFixed(0)} KB • ${doc.status === "verified" ? "Terverifikasi" : doc.status === "needs_fix" ? "Perlu Perbaikan" : "Menunggu"}` : "Belum diunggah"}
                  filled={!!doc}
                  testid={`dl-doc-${type}`}
                />
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const DlButton = ({ busy, onClick, icon, label, sublabel, primary, filled, disabled, testid }) => (
  <button
    onClick={onClick}
    disabled={busy || disabled}
    data-testid={testid}
    className={
      "flex items-start gap-3 p-3 rounded-lg border text-left transition-all " +
      (disabled
        ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
        : primary
        ? "border-blue-700 bg-blue-700 text-white hover:bg-blue-800 shadow-sm"
        : filled
        ? "border-blue-200 bg-blue-50 text-blue-900 hover:bg-blue-100"
        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
    }
  >
    <div className={"mt-0.5 " + (primary ? "text-white" : disabled ? "text-slate-400" : "text-blue-700")}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold truncate">{label}</div>
      {sublabel && <div className={"text-[11px] mt-0.5 truncate " + (primary ? "text-blue-100" : disabled ? "text-slate-400" : "text-slate-500")}>{sublabel}</div>}
    </div>
    <Download className={"w-3.5 h-3.5 mt-1 " + (primary ? "text-blue-100" : "text-slate-400")} />
  </button>
);
